import { useCallback, useEffect, useState } from "react";

import {
    useEstimateOptions,
    type EstimateOption,
} from "../../../hooks/query";
import { useEstimateContractContextManager } from "./";

export default function useEstimateOptionsSession(projectId?: string) {
    const { values, setValues, setReferenceValue } =
        useEstimateContractContextManager();
    const { useList, useFromShingles, useActivate, useUpdate } =
        useEstimateOptions();
    const listSvc = useList();
    const fromShinglesSvc = useFromShingles();
    const activateSvc = useActivate();
    const updateSvc = useUpdate();

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [compareOpen, setCompareOpen] = useState(false);

    const applyOptions = useCallback(
        (options: EstimateOption[]) => {
            const active =
                options.find((o) => o.Status === "ACTIVE") || options[0] || null;

            const resolveOpenOptionId = (previousId: string | null | undefined) => {
                if (previousId && options.some((o) => o.Id === previousId)) {
                    return previousId;
                }
                return active?.Id ?? null;
            };

            const bumpRevision = (prev: any) => ({
                ...prev,
                estimateOptions: options,
                estimateOptionId: resolveOpenOptionId(prev.estimateOptionId),
                optionCartRevision: (prev.optionCartRevision || 0) + 1,
            });

            setValues(bumpRevision);
            setReferenceValue(bumpRevision);
            setCompareOpen(options.length >= 2);
            return active?.Id ?? null;
        },
        [setValues, setReferenceValue],
    );

    const reloadOptions = useCallback(async () => {
        if (!projectId) return [];
        setLoading(true);
        setError(null);
        try {
            const options = (await listSvc.mutateAsync({
                ProjectId: projectId,
            })) as EstimateOption[];
            applyOptions(Array.isArray(options) ? options : []);
            return Array.isArray(options) ? options : [];
        } catch (err: any) {
            setError(err?.message || "Failed to load estimate options");
            return [];
        } finally {
            setLoading(false);
        }
    }, [projectId, listSvc, applyOptions]);

    useEffect(() => {
        if (!projectId) return;
        void reloadOptions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectId]);

    const syncFromShingles = useCallback(
        async (productIds: string[]) => {
            if (!projectId) return [];
            setLoading(true);
            setError(null);
            try {
                const options = (await fromShinglesSvc.mutateAsync({
                    ProjectId: projectId,
                    ProductIds: productIds,
                })) as EstimateOption[];
                applyOptions(Array.isArray(options) ? options : []);
                setCompareOpen(true);
                return Array.isArray(options) ? options : [];
            } catch (err: any) {
                setError(err?.message || "Failed to build estimate options");
                throw err;
            } finally {
                setLoading(false);
            }
        },
        [projectId, fromShinglesSvc, applyOptions],
    );

    const activateOption = useCallback(
        async (id: string) => {
            setLoading(true);
            setError(null);
            try {
                await activateSvc.mutateAsync({ Id: id });
                const options = await reloadOptions();
                setValues((prev: any) => ({
                    ...prev,
                    estimateOptionId: id,
                }));
                setReferenceValue((prev: any) => ({
                    ...prev,
                    estimateOptionId: id,
                }));
                return options;
            } catch (err: any) {
                setError(err?.message || "Failed to activate estimate option");
                throw err;
            } finally {
                setLoading(false);
            }
        },
        [activateSvc, reloadOptions, setValues, setReferenceValue],
    );

    const openOption = useCallback(
        (id: string) => {
            setValues((prev: any) => ({
                ...prev,
                estimateOptionId: id,
            }));
        },
        [setValues],
    );

    const renameOption = useCallback(
        async (id: string, label: string) => {
            await updateSvc.mutateAsync({ Id: id, Label: label });
            await reloadOptions();
        },
        [updateSvc, reloadOptions],
    );

    return {
        options: (values.estimateOptions || []) as EstimateOption[],
        estimateOptionId: values.estimateOptionId as string | null,
        loading,
        error,
        compareOpen,
        setCompareOpen,
        reloadOptions,
        syncFromShingles,
        activateOption,
        openOption,
        renameOption,
    };
}
